'use client'
import {
  Pagination as PaginationComponent,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import { useTheme } from '@/providers/Theme'
import { cn } from '@/utilities/ui'
import { ChevronFirst, ChevronLast } from 'lucide-react'
import React, { Dispatch, useEffect } from 'react'

export const PaginationProprety: React.FC<{
  className?: string
  page: number
  setPage: Dispatch<any>
  updatePagination: () => void
  totalPages: number
  dictionary?: any
}> = (props) => {
  const { className, page, totalPages, setPage, updatePagination } = props
  const hasNextPage = page < totalPages
  const hasPrevPage = page > 1

  const { theme } = useTheme()

  useEffect(() => {
    updatePagination()
  }, [page])

  const previousLabelButton =
    props.dictionary?.propertyListing?.previousButtonLabel ||
    props.dictionary?.previousButtonLabel ||
    'Previous'
  const nextLabelButton =
    props.dictionary?.propertyListing?.nextButtonLabel ||
    props.dictionary?.nextButtonLabel ||
    'Next'

  const buttonClass = cn(
    className,
    theme === 'dark'
      ? 'bg-card text-white hover:text-white'
      : ''
  )

  const activeClass = cn(
    className,
    buttonClass,
    theme === 'dark'
      ? 'bg-card hover:bg-gray-700 hover:text-white text-white'
      : ''
  )

  return (
    <div className={cn('my-12', className)}>
      <PaginationComponent>
        <PaginationContent>
          {/* First page */}
          <PaginationItem>
            <PaginationLink
              onClick={() => setPage(1)}
              disabled={!hasPrevPage}
              className={buttonClass}
            >
              <ChevronFirst className="h-4 w-4 rtl:rotate-180" />
            </PaginationLink>
          </PaginationItem>

          {/* Previous */}
          <PaginationItem>
            <PaginationPrevious
              disabled={!hasPrevPage}
              onClick={() => setPage(page - 1)}
              previousButtonLabel={previousLabelButton}
              className={buttonClass}
            />
          </PaginationItem>

          {/* First page */}
          {page > 2 && (
            <PaginationItem>
              <PaginationLink 
                onClick={() => setPage(1)}
                className={buttonClass}
                >
                  1
                </PaginationLink>
            </PaginationItem>
          )}

          {/* Ellipsis before current */}
          {page > 3 && (
            <PaginationItem>
              <PaginationEllipsis className={cn(theme === 'dark' ? 'text-white' : '')} />
            </PaginationItem>
          )}

          {/* Previous page */}
          {page > 1 && (
            <PaginationItem>
              <PaginationLink onClick={() => setPage(page - 1)} className={buttonClass}>
                {page - 1}
              </PaginationLink>
            </PaginationItem>
          )}

          {/* Current page */}
          <PaginationItem>
            <PaginationLink disabled isActive className={activeClass} onClick={() => setPage(page)}>
              {page}
            </PaginationLink>
          </PaginationItem>

          {/* Next page */}
          {page < totalPages && (
            <PaginationItem>
              <PaginationLink onClick={() => setPage(page + 1)} className={buttonClass}>
                {page + 1}
              </PaginationLink>
            </PaginationItem>
          )}

          {/* Ellipsis after current */}
          {page < totalPages - 2 && (
            <PaginationItem>
              <PaginationEllipsis className={cn(theme === 'dark' ? 'text-white' : '')} />
            </PaginationItem>
          )}

          {/* Last page */}
          {page < totalPages - 1 && (
            <PaginationItem>
              <PaginationLink onClick={() => setPage(totalPages)} className={buttonClass}>
                {totalPages}
              </PaginationLink>
            </PaginationItem>
          )}

          {/* Next */}
          <PaginationItem>
            <PaginationNext
              disabled={!hasNextPage}
              onClick={() => setPage(page + 1)}
              nextButtonLabel={nextLabelButton}
              className={buttonClass}
            />
          </PaginationItem>

           {/* Last page */}
          <PaginationItem>
            <PaginationLink
              onClick={() => setPage(totalPages)}
              disabled={!hasNextPage}
              className={buttonClass}
            >
              <ChevronLast className="h-4 w-4 rtl:rotate-180" />
            </PaginationLink>
          </PaginationItem>

        </PaginationContent>
      </PaginationComponent>
    </div>
  )
}